/**
 * Sticky Product Web Component
 * A fixed add-to-cart bar for product pages
 */
class StickyProduct extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.config = {
      position: 'bottom',
      visibility: 'after_button',
      buttonAction: 'add_to_cart',
      skipCart: false,
      scrollAnchorId: null,
      scrollOffset: 20,
      hideAtBottom: false,
      deviceType: 'desktop',
      docked: false,
      showCurrencyCode: false,
      currencyCode: null,
      productHandle: null
    };

    // State
    this.isVisible = false;
    this.currentVariantId = null;
    this.quantity = 1;
    this.isLoading = false;
    this.buyButtonObserver = null;
    this.pendingCheckout = false;
    this.buyButtonVisible = true; // Track if buy button is in viewport

    // Bound methods
    this.onScroll = this.onScroll.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
    this.handleMainVariantChange = this.handleMainVariantChange.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleExternalQuantityChange = this.handleExternalQuantityChange.bind(this);
    this.handleMainFormQuantityChange = this.handleMainFormQuantityChange.bind(this);
    this.handleSubscriptionChange = this.handleSubscriptionChange.bind(this);
    this.handleOfferPricesUpdated = this.handleOfferPricesUpdated.bind(this);

    // Flag to prevent circular quantity updates
    this.isUpdatingQuantity = false;
    // Flag to track if initial quantity has been synced from offers
    this.initialQuantitySynced = false;
    // Subscription state
    this.currentSubscriptionType = 'onetime';
    this.currentSellingPlan = null;
    this.lastOfferEventSectionId = null;
    this.lastOfferEventBlockId = null;
  }

  connectedCallback() {
    this.parseConfig();
    this.cacheElements();
    this.initializeVariantId();
    this.setupEventListeners();
    this.setupExternalQuantityListeners();
    this.subscribeToEvents();
    this.initializeVisibility();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.removeExternalQuantityListeners();
    this.unsubscribeFromEvents();
  }

  // === INITIALIZATION ===

  parseConfig() {
    this.config.position = this.dataset.position || 'bottom';
    this.config.visibility = this.dataset.visibility || 'after_button';
    this.config.buttonAction = this.dataset.buttonAction || 'add_to_cart';
    this.config.skipCart = this.dataset.skipCart === 'true';
    this.config.scrollAnchorId = this.dataset.scrollAnchorId || null;
    this.config.scrollOffset = parseInt(this.dataset.scrollOffset) || 20;
    this.config.hideAtBottom = this.dataset.hideAtBottom === 'true';
    this.config.deviceType = this.dataset.deviceType || 'desktop';
    this.config.docked = this.dataset.docked === 'true';
    this.config.showCurrencyCode = this.dataset.showCurrencyCode === 'true';
    this.config.currencyCode = this.dataset.currencyCode || window.Shopify?.currency?.active || null;
    this.config.productHandle = (this.dataset.productHandle || '').trim() || null;
    this.sectionId = this.dataset.sectionId;
  }

  cacheElements() {
    this.button = this.querySelector('[data-sticky-button]');
    this.variantSelect = this.querySelector('[data-sticky-variant-select]');
    this.quantityInput = this.querySelector('[data-sticky-quantity-input]');
    this.quantitySelect = this.querySelector('[data-sticky-quantity-select]');
    this.quantityMinusBtn = this.querySelector('[data-sticky-quantity-minus]');
    this.quantityPlusBtn = this.querySelector('[data-sticky-quantity-plus]');
    this.variantImage = this.querySelector('[data-variant-image]');
    this.buttonText = this.querySelector('[data-button-text]');
    this.buttonTextOriginalHtml = this.buttonText?.innerHTML || this.buttonText?.dataset?.originalText || '';
    this.cacheButtonPriceElements();

    // Info price elements (for quantity-responsive pricing)
    this.infoPriceContainer = this.querySelector('[data-sticky-info-price]');
    this.infoCurrentPriceElement = this.querySelector('[data-sticky-info-current-price]');
    this.infoComparePriceElement = this.querySelector('[data-sticky-info-compare-price]');

    // Initialize base prices from data attributes (info price)
    if (this.infoPriceContainer) {
      this.basePrice = parseInt(this.infoPriceContainer.dataset.basePrice) || 0;
      this.baseComparePrice = parseInt(this.infoPriceContainer.dataset.baseComparePrice) || 0;
    }

    // Initialize base prices for button price
    if (this.priceElement) {
      this.buttonBasePrice = parseInt(this.priceElement.dataset.basePrice) || 0;
      this.buttonBaseComparePrice = parseInt(this.priceElement.dataset.baseComparePrice) || 0;
    }
  }

  cacheButtonPriceElements() {
    this.priceElements = Array.from(this.querySelectorAll('[data-sticky-price]'));
    this.currentPriceElements = Array.from(this.querySelectorAll('[data-sticky-current-price]'));
    this.comparePriceElements = Array.from(this.querySelectorAll('[data-sticky-compare-price]'));

    this.priceElement = this.priceElements[0] || null;
    this.currentPriceElement = this.currentPriceElements[0] || null;
    this.comparePriceElement = this.comparePriceElements[0] || null;
  }

  initializeVariantId() {
    // Try to get variant from sticky product's select
    if (this.variantSelect) {
      this.currentVariantId = this.variantSelect.value;
    } else {
      // Get from main product form
      const mainVariantInput = document.querySelector(
        'product-form input[name="id"], ' +
        'form[action*="/cart/add"] input[name="id"]'
      );
      if (mainVariantInput) {
        this.currentVariantId = mainVariantInput.value;
      }
    }
  }

  setupEventListeners() {
    // Scroll listener for hide at bottom
    if (this.config.hideAtBottom) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    // Variant selector
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.onVariantChange);
    }

    // Quantity input
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.onQuantityChange);
    }

    // Quantity select (dropdown)
    if (this.quantitySelect) {
      this.quantitySelect.addEventListener('change', this.onQuantityChange);
    }

    // Quantity plus/minus buttons
    if (this.quantityMinusBtn) {
      this.quantityMinusBtn.addEventListener('click', () => this.adjustQuantity(-1));
    }
    if (this.quantityPlusBtn) {
      this.quantityPlusBtn.addEventListener('click', () => this.adjustQuantity(1));
    }

    // Button click
    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick);
    }
  }

  removeEventListeners() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.onDockedScroll) {
      window.removeEventListener('scroll', this.onDockedScroll);
    }
    if (this.onDockedResize) {
      window.removeEventListener('resize', this.onDockedResize);
    }
    if (this.dockedPlaceholder && this.dockedPlaceholder.parentNode) {
      this.dockedPlaceholder.parentNode.removeChild(this.dockedPlaceholder);
    }
    if (this.variantSelect) {
      this.variantSelect.removeEventListener('change', this.onVariantChange);
    }
    if (this.quantityInput) {
      this.quantityInput.removeEventListener('change', this.onQuantityChange);
    }
    if (this.quantitySelect) {
      this.quantitySelect.removeEventListener('change', this.onQuantityChange);
    }
    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick);
    }
    if (this.buyButtonObserver) {
      this.buyButtonObserver.disconnect();
    }
  }

  setupExternalQuantityListeners() {
    // Listen for Offers block quantity changes
    document.addEventListener('offers:quantity-changed', this.handleExternalQuantityChange);

    // Listen for Subscriptions block type changes
    document.addEventListener('subscription:type-changed', this.handleSubscriptionChange);
    document.addEventListener('offers:prices-updated', this.handleOfferPricesUpdated);

    // Find and listen to main form quantity input
    this.mainQuantityInput = document.querySelector(
      'quantity-input input[name="quantity"], ' +
      '.product-form__quantity input[name="quantity"], ' +
      '.product-form__input input[name="quantity"], ' +
      'product-form input[name="quantity"], ' +
      'form[action*="/cart/add"] input[name="quantity"], ' +
      '.quantity__input[name="quantity"]'
    );

    // Also try to find quantity select (dropdown style)
    if (!this.mainQuantityInput) {
      this.mainQuantityInput = document.querySelector(
        '.quantity--dropdown select[name="quantity"], ' +
        'product-form select[name="quantity"], ' +
        'form[action*="/cart/add"] select[name="quantity"]'
      );
    }

    if (this.mainQuantityInput) {
      this.mainQuantityInput.addEventListener('change', this.handleMainFormQuantityChange);
    }

    // Sync initial quantity after Alpine.js has initialized the Offers block
    // Alpine's init() runs during initialization and sets the preselected offer quantity
    // We need to wait for that to complete before reading the value
    this.scheduleInitialQuantitySync();
  }

  scheduleInitialQuantitySync() {
    // Multiple timing strategies to catch Alpine initialization:

    // 1. After DOMContentLoaded + small delay for Alpine to process
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.syncInitialQuantity(), 100);
      });
    } else {
      // DOM already loaded, check soon
      setTimeout(() => this.syncInitialQuantity(), 50);
    }

    // 2. After Alpine has initialized all components
    document.addEventListener('alpine:initialized', () => {
      // Small delay to ensure init() methods have completed
      setTimeout(() => this.syncInitialQuantity(), 50);
    }, { once: true });

    // 3. Fallback: longer delay to ensure everything is settled
    // This catches cases where Alpine loads asynchronously
    setTimeout(() => this.syncInitialQuantity(), 500);
  }

  syncInitialQuantity() {
    if (this.isUpdatingQuantity || this.initialQuantitySynced) return;

    // Strategy 1: Check the offers block directly for selected offer quantity
    // This is the most reliable since it reads the actual selected offer state
    const selectedOffer = document.querySelector('.offer.is-selected[data-quantity]');
    if (selectedOffer) {
      const offerQty = parseInt(selectedOffer.dataset.quantity) || 1;
      if (offerQty !== this.quantity) {
        this.updateQuantityDisplay(offerQty);
        this.initialQuantitySynced = true;
        return;
      } else if (offerQty > 1) {
        this.initialQuantitySynced = true;
        return;
      }
    }

    // Strategy 2: Re-find the quantity input in case it was dynamically added
    if (!this.mainQuantityInput) {
      // Try specific selectors first
      this.mainQuantityInput = document.querySelector(
        'quantity-input input[name="quantity"], ' +
        '.product-form__quantity input[name="quantity"], ' +
        '.product-form__input input[name="quantity"], ' +
        'product-form input[name="quantity"], ' +
        'form[action*="/cart/add"] input[name="quantity"], ' +
        '.quantity__input[name="quantity"], ' +
        '.quantity--dropdown select[name="quantity"], ' +
        'product-form select[name="quantity"], ' +
        'form[action*="/cart/add"] select[name="quantity"]'
      );

      // Fallback: find any quantity input in the main product section
      // (matches how the offers block finds the input)
      if (!this.mainQuantityInput) {
        const productSection = document.querySelector('.shopify-section-main-product, [data-section-type="product"]');
        if (productSection) {
          this.mainQuantityInput = productSection.querySelector('[name="quantity"]');
        }
      }

      if (this.mainQuantityInput) {
        this.mainQuantityInput.addEventListener('change', this.handleMainFormQuantityChange);
      }
    }

    // Read current quantity from main form
    if (this.mainQuantityInput) {
      const currentQty = parseInt(this.mainQuantityInput.value) || 1;
      if (currentQty !== this.quantity) {
        this.updateQuantityDisplay(currentQty);
        this.initialQuantitySynced = true;
      } else if (currentQty > 1) {
        // If quantity is already > 1 and matches, we've successfully synced
        this.initialQuantitySynced = true;
      }
    }
  }

  removeExternalQuantityListeners() {
    document.removeEventListener('offers:quantity-changed', this.handleExternalQuantityChange);
    document.removeEventListener('subscription:type-changed', this.handleSubscriptionChange);
    document.removeEventListener('offers:prices-updated', this.handleOfferPricesUpdated);

    if (this.mainQuantityInput) {
      this.mainQuantityInput.removeEventListener('change', this.handleMainFormQuantityChange);
    }
  }

  handleExternalQuantityChange(event) {
    if (this.isUpdatingQuantity) return;

    const { quantity } = event.detail;
    if (quantity && quantity !== this.quantity) {
      this.updateQuantityDisplay(quantity);
    }

    // Mark initial sync as complete since we received the offers event
    this.initialQuantitySynced = true;
  }

  handleMainFormQuantityChange() {
    if (this.isUpdatingQuantity) return;

    const qty = parseInt(this.mainQuantityInput.value) || 1;
    if (qty !== this.quantity) {
      this.updateQuantityDisplay(qty);
    }
  }

  handleSubscriptionChange(event) {
    const { type, sellingPlan } = event.detail || {};
    this.currentSubscriptionType = type || 'onetime';
    this.currentSellingPlan = sellingPlan;

    // Update prices based on subscription type
    this.updateButtonPriceForSubscription();
  }

  handleOfferPricesUpdated(event) {
    const detail = event?.detail || {};
    const selectedEventOffer = this.getSelectedOfferFromEvent(detail);

    if (selectedEventOffer && !this.offerMatchesConfiguredProduct(selectedEventOffer)) {
      return;
    }

    if (detail.sectionId) {
      this.lastOfferEventSectionId = String(detail.sectionId);
    }
    if (detail.blockId) {
      this.lastOfferEventBlockId = String(detail.blockId);
    }

    const quantity = parseInt(detail.quantity, 10);
    if (Number.isFinite(quantity) && quantity > 0) {
      this.quantity = quantity;
    }

    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();
  }

  offerMatchesConfiguredProduct(offer) {
    if (!offer) return false;
    if (!this.config.productHandle) return true;

    const offerHandle = String(offer.dataset.productHandle || '').trim();
    return !offerHandle || offerHandle === this.config.productHandle;
  }

  getSelectedOfferFromEvent(detail) {
    const sectionId = String(detail?.sectionId || '').trim();
    const blockId = String(detail?.blockId || '').trim();

    let selector = '.offer.is-selected';
    if (blockId) {
      selector += `[data-block-id="${blockId.replace(/"/g, '\\"')}"]`;
    }

    if (sectionId) {
      return document.querySelector(`#shopify-section-${sectionId} ${selector}`);
    }

    if (blockId) {
      return document.querySelector(selector);
    }

    return null;
  }

  getSelectedOfferElement() {
    const candidates = [];

    if (this.lastOfferEventSectionId) {
      candidates.push(
        document.querySelector(`#shopify-section-${this.lastOfferEventSectionId} .offer.is-selected`)
      );
    }

    if (this.lastOfferEventBlockId) {
      candidates.push(
        document.querySelector(
          `.offer.is-selected[data-block-id="${this.lastOfferEventBlockId.replace(/"/g, '\\"')}"]`
        )
      );
    }

    if (this.sectionId) {
      candidates.push(document.querySelector(`#shopify-section-${this.sectionId} .offer.is-selected`));
    }

    for (const candidate of candidates) {
      if (this.offerMatchesConfiguredProduct(candidate)) {
        return candidate;
      }
    }

    const selectedOffers = Array.from(document.querySelectorAll('.offer.is-selected'));
    if (selectedOffers.length === 0) return null;

    const matchingOffer = selectedOffers.find((offer) => this.offerMatchesConfiguredProduct(offer));
    if (matchingOffer) return matchingOffer;

    return this.config.productHandle ? null : selectedOffers[0];
  }

  getSelectedOfferComputedTotals() {
    const selectedOffer = this.getSelectedOfferElement();
    if (!selectedOffer) return null;

    const totalPrice = parseInt(selectedOffer.dataset.computedTotalPrice, 10);
    const comparePrice = parseInt(selectedOffer.dataset.computedComparePrice, 10);
    const quantity = Math.max(parseInt(selectedOffer.dataset.quantity, 10) || this.quantity || 1, 1);

    if (!Number.isFinite(totalPrice)) return null;

    return {
      quantity,
      totalPrice: Math.max(0, totalPrice),
      comparePrice: Number.isFinite(comparePrice) ? Math.max(0, comparePrice) : Math.max(0, totalPrice)
    };
  }

  updateButtonPriceForSubscription() {
    const offerTotals = this.getSelectedOfferComputedTotals();
    if (offerTotals) {
      this.quantity = offerTotals.quantity;
      this.updateButtonPriceForQuantity();
      return;
    }

    if (this.buttonBasePrice === undefined || this.buttonBasePrice === null || this.currentPriceElements.length === 0) return;

    if (this.currentSubscriptionType === 'subscribe' && this.currentSellingPlan) {
      const adjustment = this.currentSellingPlan.price_adjustments?.[0];
      let subscriptionPrice = this.buttonBasePrice;

      if (adjustment?.value_type === 'percentage') {
        subscriptionPrice = Math.round(this.buttonBasePrice * (100 - adjustment.value) / 100);
      } else if (adjustment?.value_type === 'fixed_amount') {
        subscriptionPrice = this.buttonBasePrice - adjustment.value;
      }

      const formattedSubscriptionPrice = this.formatMoney(subscriptionPrice * this.quantity);
      const formattedComparePrice = this.formatMoney(this.buttonBasePrice * this.quantity);

      this.currentPriceElements.forEach((priceEl) => {
        priceEl.textContent = formattedSubscriptionPrice;
      });
      this.comparePriceElements.forEach((compareEl) => {
        compareEl.textContent = formattedComparePrice;
        compareEl.style.display = '';
      });
    } else {
      // One-time purchase - show regular price
      const formattedOneTimePrice = this.formatMoney(this.buttonBasePrice * this.quantity);
      this.currentPriceElements.forEach((priceEl) => {
        priceEl.textContent = formattedOneTimePrice;
      });
      if (this.buttonBaseComparePrice <= this.buttonBasePrice) {
        this.comparePriceElements.forEach((compareEl) => {
          compareEl.style.display = 'none';
        });
      }
    }
  }

  updateQuantityDisplay(qty) {
    this.quantity = qty;

    // Update sticky bar's quantity selector UI
    if (this.quantityInput && parseInt(this.quantityInput.value) !== qty) {
      this.quantityInput.value = qty;
    }
    if (this.quantitySelect && parseInt(this.quantitySelect.value) !== qty) {
      this.quantitySelect.value = qty;
    }

    // Update prices
    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();
  }

  syncToMainForm(qty) {
    if (!this.mainQuantityInput || this.isUpdatingQuantity) return;

    this.isUpdatingQuantity = true;

    if (parseInt(this.mainQuantityInput.value) !== qty) {
      this.mainQuantityInput.value = qty;
      this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Reset flag after a short delay to allow event propagation
    setTimeout(() => {
      this.isUpdatingQuantity = false;
    }, 50);
  }

  subscribeToEvents() {
    // Subscribe to variant changes from main product
    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleMainVariantChange
      );

      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.handleCartUpdate
      );
    }
  }

  unsubscribeFromEvents() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initializeVisibility() {
    // Docked mode: use scroll-based sticking
    if (this.config.docked) {
      this.isVisible = true;
      this.setupDockedMode();
      return;
    }

    if (this.config.visibility === 'always') {
      // Small delay to allow CSS transition
      requestAnimationFrame(() => {
        this.show();
      });
    } else {
      // Set up observer for buy button
      this.setupBuyButtonObserver();
    }
  }

  setupDockedMode() {
    this.isStuck = false;
    this.isDocked = false;
    this.dockedScrollTicking = false;

    // Create placeholder to prevent content jump
    this.dockedPlaceholder = document.createElement('div');
    this.dockedPlaceholder.style.margin = '0';
    this.dockedPlaceholder.style.padding = '0';
    this.dockedPlaceholder.style.border = 'none';
    this.dockedPlaceholder.style.visibility = 'hidden';
    this.parentNode.insertBefore(this.dockedPlaceholder, this.nextSibling);

    if (this.config.position === 'top') {
      // Top mode: starts in document flow, sticks when scrolled past
      requestAnimationFrame(() => {
        const rect = this.getBoundingClientRect();
        this.dockedHeight = Math.round(rect.height);
        this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
        this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
        this.dockedPlaceholder.style.display = 'none';
        this.startDockedListeners();
      });
    } else {
      // Cache element height for bottom mode
      this.dockedHeight = this.offsetHeight;
      // Bottom mode: starts fixed, docks when scrolled to
      this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
      this.dockedPlaceholder.style.display = 'block';
      this.startDockedListeners();
    }
  }

  startDockedListeners() {
    // Bind handlers
    this.onDockedScroll = this.handleDockedScroll.bind(this);
    this.onDockedResize = this.handleDockedResize.bind(this);

    window.addEventListener('scroll', this.onDockedScroll, { passive: true });
    window.addEventListener('resize', this.onDockedResize, { passive: true });

    // Initial check
    this.handleDockedScroll();
  }

  handleDockedResize() {
    // Recalculate position and height on resize
    const rect = this.getBoundingClientRect();
    if (this.config.position === 'top' && !this.isStuck) {
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
      this.dockedHeight = Math.round(rect.height);
    } else {
      this.dockedHeight = this.offsetHeight;
    }
    this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
  }

  handleDockedScroll() {
    if (!this.config.docked) return;

    // Skip if element is hidden (e.g., wrong viewport in theme editor)
    // This is needed because desktop/mobile are separate elements with separate placeholders
    if (this.offsetHeight === 0) {
      // Element is hidden - make sure placeholder is also hidden
      if (this.isStuck) {
        this.isStuck = false;
        this.classList.remove('is-stuck');
        this.dockedPlaceholder.style.display = 'none';
      }
      return;
    }

    // Throttle with requestAnimationFrame to prevent excessive calculations
    if (!this.dockedScrollTicking) {
      this.dockedScrollTicking = true;
      requestAnimationFrame(() => {
        if (this.config.position === 'top') {
          this.handleTopDockedScroll();
        } else {
          this.handleBottomDockedScroll();
        }
        this.dockedScrollTicking = false;
      });
    }
  }

  handleTopDockedScroll() {
    // Round scroll position to avoid sub-pixel jitter
    const scrollY = Math.round(window.scrollY);

    // Get the current header offset - this is where the sticky section will be positioned
    // When header is hidden: offset = 0, when header is visible/sticky: offset = header height
    const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-offset')) || 0;

    // Calculate the stick threshold accounting for header offset
    // The section should stick when its viewport position reaches the header offset
    // Section viewport top = dockedOriginalTop - scrollY
    // Stick when: dockedOriginalTop - scrollY <= headerOffset
    // Which means: scrollY >= dockedOriginalTop - headerOffset
    const stickThreshold = this.dockedOriginalTop - headerOffset;

    // Hysteresis buffer: require scrolling a bit past threshold to unstick
    // This prevents rapid toggling when at the boundary
    const unstickBuffer = 5;

    if (!this.isStuck) {
      // Check if we should stick
      if (scrollY >= stickThreshold) {
        this.isStuck = true;
        this.dockedPlaceholder.style.display = 'block';
        this.classList.add('is-stuck');
      }
    } else {
      // Already stuck - check if we should unstick
      // Recalculate threshold in case header offset changed while stuck
      const currentStickThreshold = this.dockedOriginalTop - headerOffset;
      if (scrollY < currentStickThreshold - unstickBuffer) {
        this.isStuck = false;
        this.dockedPlaceholder.style.display = 'none';
        this.classList.remove('is-stuck');
      }
    }
  }

  handleBottomDockedScroll() {
    const viewportHeight = window.innerHeight;
    const buffer = 5;

    if (!this.isDocked) {
      // Bar is fixed at bottom of viewport
      // Its visual top position is: viewportHeight - barHeight
      const barVisualTop = viewportHeight - this.dockedHeight;

      // Get placeholder position (where bar should dock)
      const placeholderRect = this.dockedPlaceholder.getBoundingClientRect();

      // Dock when placeholder has scrolled up to meet the bar's current position
      // This creates a seamless visual transition
      if (placeholderRect.top <= barVisualTop) {
        this.dockScrollThreshold = window.scrollY;
        this.isDocked = true;
        this.dockedPlaceholder.style.display = 'none';
        this.classList.add('is-docked');
      }
    } else {
      // Bar is docked - undock when scrolled back above threshold
      if (window.scrollY < this.dockScrollThreshold - buffer) {
        this.isDocked = false;
        this.dockedPlaceholder.style.display = 'block';
        this.classList.remove('is-docked');
      }
    }
  }

  setupBuyButtonObserver() {
    // Find the first buy button on the page
    const buyButton = document.querySelector(
      'product-form .product-form__submit, ' +
      '.product-buy-button-block button[type="submit"], ' +
      '[data-sticky-trigger], ' +
      'form[action*="/cart/add"] button[type="submit"]'
    );

    if (!buyButton) {
      // No buy button found, show sticky bar
      this.buyButtonVisible = false; // Treat as if buy button is not visible
      requestAnimationFrame(() => {
        this.show();
      });
      return;
    }

    // Create intersection observer
    this.buyButtonObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          // Buy button visible in viewport - hide sticky bar
          this.buyButtonVisible = true;
          this.hide();
        } else {
          // Buy button not visible - check if it's above or below viewport
          const rect = entry.boundingClientRect;
          const isBelowViewport = rect.top >= window.innerHeight;

          if (isBelowViewport) {
            // Haven't scrolled to buy button yet - keep sticky hidden
            this.buyButtonVisible = true;
            this.hide();
          } else {
            // Scrolled past buy button - show sticky
            this.buyButtonVisible = false;
            if (!this.shouldHideAtBottom()) {
              this.show();
            }
          }
        }
      },
      {
        threshold: 0,
        rootMargin: '-50px 0px 0px 0px'
      }
    );

    this.buyButtonObserver.observe(buyButton);
  }

  // === EVENT HANDLERS ===

  onScroll() {
    if (!this.config.hideAtBottom) return;

    if (this.shouldHideAtBottom()) {
      this.hide();
    } else if (this.shouldBeVisible()) {
      this.show();
    }
  }

  onVariantChange(event) {
    const newVariantId = event.target.value;
    const selectedOption = event.target.selectedOptions[0];

    this.currentVariantId = newVariantId;

    // Update price display
    if (selectedOption) {
      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      this.updatePriceDisplay(price, comparePrice);

      // Update image
      const imageUrl = selectedOption.dataset.image;
      if (imageUrl && this.variantImage) {
        this.variantImage.src = imageUrl;
      }
    }

    // Sync with main product
    this.syncMainProductVariant(newVariantId);

    // Update button state based on availability
    const isDisabled = selectedOption && selectedOption.disabled;
    if (this.button) {
      this.button.disabled = isDisabled;
    }
  }

  onQuantityChange(event) {
    const newQty = parseInt(event.target.value) || 1;
    this.quantity = newQty;
    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();

    // Sync to main form so cart addition uses correct quantity
    this.syncToMainForm(newQty);
  }

  adjustQuantity(delta) {
    const newQuantity = Math.max(1, this.quantity + delta);
    this.quantity = newQuantity;
    if (this.quantityInput) {
      this.quantityInput.value = newQuantity;
    }
    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();

    // Sync to main form so cart addition uses correct quantity
    this.syncToMainForm(newQuantity);
  }

  updateInfoPriceForQuantity() {
    const offerTotals = this.getSelectedOfferComputedTotals();
    if (offerTotals) {
      this.quantity = offerTotals.quantity;

      if (this.infoCurrentPriceElement) {
        this.infoCurrentPriceElement.textContent = this.formatMoney(offerTotals.totalPrice);
      }

      if (this.infoComparePriceElement) {
        if (offerTotals.comparePrice > offerTotals.totalPrice) {
          this.infoComparePriceElement.textContent = this.formatMoney(offerTotals.comparePrice);
          this.infoComparePriceElement.style.display = '';
        } else {
          this.infoComparePriceElement.style.display = 'none';
        }
      }
      return;
    }

    if (!this.infoPriceContainer || !this.basePrice) return;

    const totalPrice = this.basePrice * this.quantity;
    const totalComparePrice = this.baseComparePrice * this.quantity;

    if (this.infoCurrentPriceElement) {
      this.infoCurrentPriceElement.textContent = this.formatMoney(totalPrice);
    }

    if (this.infoComparePriceElement) {
      if (this.baseComparePrice > this.basePrice) {
        this.infoComparePriceElement.textContent = this.formatMoney(totalComparePrice);
        this.infoComparePriceElement.style.display = '';
      } else {
        this.infoComparePriceElement.style.display = 'none';
      }
    }
  }

  updateButtonPriceForQuantity() {
    const offerTotals = this.getSelectedOfferComputedTotals();
    if (offerTotals) {
      this.quantity = offerTotals.quantity;

      this.currentPriceElements.forEach((currentPriceElement) => {
        currentPriceElement.textContent = this.formatMoney(offerTotals.totalPrice);
      });

      this.comparePriceElements.forEach((comparePriceElement) => {
        if (offerTotals.comparePrice > offerTotals.totalPrice) {
          comparePriceElement.textContent = this.formatMoney(offerTotals.comparePrice);
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      });
      return;
    }

    if (this.priceElements.length === 0 || this.buttonBasePrice === undefined || this.buttonBasePrice === null) return;

    const totalPrice = this.buttonBasePrice * this.quantity;
    const totalComparePrice = this.buttonBaseComparePrice * this.quantity;

    this.currentPriceElements.forEach((currentPriceElement) => {
      currentPriceElement.textContent = this.formatMoney(totalPrice);
    });

    this.comparePriceElements.forEach((comparePriceElement) => {
      if (this.buttonBaseComparePrice && this.buttonBaseComparePrice > this.buttonBasePrice) {
        comparePriceElement.textContent = this.formatMoney(totalComparePrice);
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    });
  }

  onButtonClick(event) {
    event.preventDefault();

    if (this.isLoading || this.button.disabled) return;

    if (this.config.buttonAction === 'scroll') {
      this.scrollToAnchor();
    } else {
      this.addToCart();
    }
  }

  handleMainVariantChange(event) {
    const { data } = event;
    if (!data || !data.variant) return;

    const variant = data.variant;
    this.currentVariantId = variant.id;

    // Update variant selector
    if (this.variantSelect && this.variantSelect.value !== String(variant.id)) {
      this.variantSelect.value = variant.id;
    }

    // Update base prices for info price
    this.basePrice = variant.price;
    this.baseComparePrice = variant.compare_at_price || 0;

    // Update info price container data attributes
    if (this.infoPriceContainer) {
      this.infoPriceContainer.dataset.basePrice = variant.price;
      this.infoPriceContainer.dataset.baseComparePrice = variant.compare_at_price || 0;
    }

    // Update base prices for button price
    this.buttonBasePrice = variant.price;
    this.buttonBaseComparePrice = variant.compare_at_price || 0;

    // Update button price container data attributes
    this.priceElements.forEach((priceElement) => {
      priceElement.dataset.basePrice = variant.price;
      priceElement.dataset.baseComparePrice = variant.compare_at_price || 0;
    });

    // Update info price display with quantity
    this.updateInfoPriceForQuantity();

    // Update button price display with quantity
    this.updateButtonPriceForQuantity();

    // Update image
    if (variant.featured_image && this.variantImage) {
      this.variantImage.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_100x$1');
    }

    // Update button state
    if (this.button) {
      this.button.disabled = !variant.available;

      // Update button text
      if (this.buttonText) {
        if (!variant.available) {
          this.buttonText.innerHTML = window.variantStrings?.soldOutHtml || window.variantStrings?.soldOut || 'Sold Out';
          this.button.classList.add('is-sold-out');
          this.cacheButtonPriceElements();
        } else {
          this.button.classList.remove('is-sold-out');
          const originalText = this.buttonTextOriginalHtml || this.buttonText.dataset.originalText;
          if (originalText) {
            this.buttonText.innerHTML = originalText;
          }
          this.cacheButtonPriceElements();
          this.updateButtonPriceForQuantity();
        }
      }
    }
  }

  handleCartUpdate(event) {
    if (this.pendingCheckout && this.config.skipCart) {
      this.pendingCheckout = false;
      window.location.href = '/checkout';
    }
  }

  // === VISIBILITY METHODS ===

  show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.classList.add('is-visible');
  }

  hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.classList.remove('is-visible');
  }

  shouldBeVisible() {
    if (this.config.visibility === 'always') return true;

    // Check if buy button is NOT visible (meaning sticky bar should show)
    return !this.buyButtonVisible;
  }

  shouldHideAtBottom() {
    if (!this.config.hideAtBottom) return false;

    const scrollPosition = window.scrollY + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const threshold = 150;

    return (pageHeight - scrollPosition) < threshold;
  }

  // === SYNC METHODS ===

  syncMainProductVariant(variantId) {
    // Find main product's variant selector/input
    const mainVariantInput = document.querySelector(
      'product-form input[name="id"], ' +
      'form[action*="/cart/add"] input[name="id"]'
    );

    if (mainVariantInput && mainVariantInput.value !== variantId) {
      mainVariantInput.value = variantId;

      // Try to trigger variant change in main product
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        // Find the select and update it
        const select = variantSelects.querySelector('select[name="id"]');
        if (select && select.value !== variantId) {
          select.value = variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  // === UPDATE METHODS ===

  updatePriceDisplay(price, comparePrice) {
    this.currentPriceElements.forEach((currentPriceElement) => {
      currentPriceElement.textContent = this.formatMoney(price);
    });

    this.comparePriceElements.forEach((comparePriceElement) => {
      if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
        comparePriceElement.textContent = this.formatMoney(comparePrice);
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    });
  }

  // === ACTION METHODS ===

  async addToCart() {
    if (this.isLoading || !this.currentVariantId) return;

    this.setLoading(true);

    const item = {
      id: parseInt(this.currentVariantId),
      quantity: this.quantity
    };

    // Include selling plan when subscription is active
    if (this.currentSubscriptionType === 'subscribe' && this.currentSellingPlan) {
      item.selling_plan = this.currentSellingPlan.id;
    }

    try {
      const cartDrawer = document.querySelector('sugar-cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.().map(s => s.id) || ['sugar-cart-drawer', 'cart-icon-bubble'];

      // Set context for gift managers so they inject free gifts into this request
      this.setGiftContext();

      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [item],
          sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Add to cart failed');
      const data = await response.json();

      if (data.status) {
        this.handleError(data.description || data.message);
        return;
      }

      // Skip cart → go straight to checkout
      if (this.config.skipCart) {
        window.location.href = '/checkout';
        return;
      }

      // Publish cart update (cart drawer subscription skips 'sticky-product' source)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sticky-product',
          productVariantId: this.currentVariantId,
          cartData: data
        });
      }

      // Render cart drawer via Section Rendering API response
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

    } catch (error) {
      console.error('Sticky Product - Add to cart error:', error);
      this.handleError('Unable to add to cart');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Set context on gift managers (gift-list.js, gift-unlocks.js) so they
   * inject free gift items into the upcoming cart/add fetch request.
   * The sticky product lives in a separate section, so we point the context
   * at the main product section where the gift blocks actually reside.
   */
  setGiftContext() {
    const mainForm = document.querySelector('product-form form, form[action*="/cart/add"]');
    if (!mainForm) return;

    const sectionEl = mainForm.closest('.shopify-section');
    const sectionId = sectionEl?.id?.replace(/^shopify-section-/, '') || null;
    const productInfo = mainForm.closest('product-info') || sectionEl?.querySelector('product-info');
    const productId = productInfo?.dataset?.productId || null;

    if (!sectionId && !productId) return;

    const context = { sectionId, productId, capturedAt: Date.now() };

    if (window.giftListManager) {
      window.giftListManager.lastSubmitContext = context;
    }
    if (window.giftUnlocksManager) {
      window.giftUnlocksManager.lastSubmitContext = context;
    }
  }

  scrollToAnchor() {
    if (!this.config.scrollAnchorId) return;

    const target = document.getElementById(this.config.scrollAnchorId);
    if (target) {
      // Account for sticky header and scroll offset
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0');
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - this.config.scrollOffset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }

  // === UTILITY METHODS ===

  setLoading(loading) {
    this.isLoading = loading;

    if (this.button) {
      this.button.disabled = loading;
      this.button.classList.toggle('loading', loading);
    }
  }

  handleError(message) {
    console.error('Sticky Product Error:', message);
    // Could show a toast notification here
  }

  formatMoney(cents) {
    if (this.config.showCurrencyCode) {
      const currencyCode = this.config.currencyCode;
      if (currencyCode) {
        const amount = (parseInt(cents, 10) / 100).toFixed(2);
        const fmtOpts = { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' };
        return `${new Intl.NumberFormat(window.Shopify?.locale || 'en-US', fmtOpts).format(amount)} ${currencyCode}`;
      }
    }

    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const amount = (parseInt(cents) / 100).toFixed(2);
    return '$' + amount;
  }
}

// Register the custom element
if (!customElements.get('sticky-product')) {
  customElements.define('sticky-product', StickyProduct);
}
